import { Injectable, OnDestroy, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

interface TrackedDialog {
  container: HTMLElement;
  dialog: HTMLElement | null;
  content: Element | null;
  order: number;
  originalHeight: string | null;
  originalMinHeight: string | null;
  originalWidth: string | null;
  originalMinWidth: string | null;
}

const STACK_OFFSET_PX = 24;
const MAX_STACK_LEVELS = 3;
const INACTIVE_DIALOG_CLASS = 'bd-dialog-stacked-inactive';
const STACKED_DIALOG_CLASS = 'bd-dialog-stacked';
const DIMENSIONS_SYNCED_CLASS = 'bd-dialog-dimensions-synced';
// PrimeNG v21 appends p-dynamicdialog host elements to body,
// with the p-dialog-mask nested inside (appendTo="self")
const DIALOG_MASK_CLASS = 'p-dialog-mask';
const DIALOG_CLASS = 'p-dialog';
const DIALOG_CLOSE_BUTTON_CLASS = 'p-dialog-close-button';
const DYNAMIC_DIALOG_TAG = 'p-dynamicdialog';
const HEIGHT_SYNC_DEBOUNCE_MS = 150;

@Injectable()
export class DialogStackService implements OnDestroy {
  private resizeObserver: ResizeObserver | null = null;
  private mutationObserver: MutationObserver | null = null;
  private trackedDialogs: TrackedDialog[] = [];
  private dialogOrder = 0;
  private readonly _isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private initialized = false;
  private styleElement: HTMLStyleElement | null = null;
  private heightSyncSubject = new Subject<void>();
  private heightSyncSubscription: { unsubscribe(): void } | null = null;

  init(): void {
    if (this.initialized || !this._isBrowser) {
      return;
    }

    this.injectStyles();
    this.initResizeObserver();
    this.initMutationObserver();
    this.initHeightSyncSubscription();

    this.initialized = true;
  }

  private initHeightSyncSubscription(): void {
    this.heightSyncSubscription = this.heightSyncSubject
      .pipe(debounceTime(HEIGHT_SYNC_DEBOUNCE_MS))
      .subscribe(() => this.synchronizeDialogDimensions());
  }

  private injectStyles(): void {
    if (this.styleElement) {
      return;
    }

    this.styleElement = document.createElement('style');
    this.styleElement.id = 'bd-dialog-stacked-styles';
    this.styleElement.textContent = `
      .${STACKED_DIALOG_CLASS} .${DIALOG_CLASS} {
        transition: margin 0.2s ease-out, height 0.2s ease-out, min-height 0.2s ease-out, width 0.2s ease-out, min-width 0.2s ease-out !important;
      }

      .${INACTIVE_DIALOG_CLASS} {
        pointer-events: none !important;
      }

      .${INACTIVE_DIALOG_CLASS} .${DIALOG_CLASS} {
        pointer-events: none !important;
      }

      .${INACTIVE_DIALOG_CLASS} .p-dialog-content {
        overflow: hidden !important;
      }

      .${INACTIVE_DIALOG_CLASS} .${DIALOG_CLOSE_BUTTON_CLASS} {
        display: none !important;
      }

      .${DIMENSIONS_SYNCED_CLASS} .${DIALOG_CLASS} {
        transition: height 0.2s ease-out, min-height 0.2s ease-out, width 0.2s ease-out, min-width 0.2s ease-out !important;
      }
    `;
    document.head.appendChild(this.styleElement);
  }

  private initResizeObserver(): void {
    this.resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        this.checkOverflow(entry.target);
        // Trigger height sync when any dialog content changes size
        this.heightSyncSubject.next();
      });
    });
  }

  private initMutationObserver(): void {
    this.mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof Element) {
            this.checkForDialogs(node);
          }
        });

        mutation.removedNodes.forEach((node) => {
          if (node instanceof Element) {
            this.cleanupRemovedDialogs(node);
          }
        });
      });
    });

    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: false,
    });
  }

  private checkForDialogs(element: Element): void {
    // PrimeNG v21: DialogService appends p-dynamicdialog host elements to body.
    // The p-dialog-mask is nested inside (appendTo="self") and may not be
    // rendered yet when the host element is first added to the DOM.
    if (element.tagName?.toLowerCase() === DYNAMIC_DIALOG_TAG) {
      this.processDialogHost(element as HTMLElement);
      return;
    }

    this.registerDialogElements(element);
  }

  /**
   * Processes a p-dynamicdialog host element added to the DOM.
   * Searches for dialog elements inside and retries once via
   * requestAnimationFrame if the mask has not rendered yet.
   */
  private processDialogHost(host: HTMLElement): void {
    const mask = host.querySelector(`.${DIALOG_MASK_CLASS}`);
    if (mask) {
      this.registerDialogElements(host);
      return;
    }

    // Mask not rendered yet — retry after next animation frame
    requestAnimationFrame(() => {
      if (document.body.contains(host)) {
        this.registerDialogElements(host);
      }
    });
  }

  /**
   * Finds and registers p-dialog-mask, p-dialog, and p-dialog-content
   * elements within the given element (or the element itself).
   */
  private registerDialogElements(element: Element): void {
    if (element.classList?.contains(DIALOG_MASK_CLASS)) {
      this.observeDialogContainer(element as HTMLElement);
    }

    element.querySelectorAll(`.${DIALOG_MASK_CLASS}`).forEach((container) => {
      this.observeDialogContainer(container as HTMLElement);
    });

    if (element.classList?.contains(DIALOG_CLASS)) {
      this.linkDialogToContainer(element as HTMLElement);
    }

    element.querySelectorAll(`.${DIALOG_CLASS}`).forEach((dialog) => {
      this.linkDialogToContainer(dialog as HTMLElement);
    });

    if (element.classList?.contains('p-dialog-content')) {
      this.observeDialogContent(element);
    }

    element.querySelectorAll('.p-dialog-content').forEach((content) => {
      this.observeDialogContent(content);
    });
  }

  private observeDialogContainer(container: HTMLElement): void {
    const existingTracked = this.trackedDialogs.find(
      (d) => d.container === container,
    );
    if (existingTracked) {
      return;
    }

    this.dialogOrder++;
    const dialog = container.querySelector(
      `.${DIALOG_CLASS}`,
    ) as HTMLElement | null;

    // Capture original dimensions from inline styles (PrimeNG sets these from
    // config.width / config.height; absent values stay null and we'll clear
    // any synced inline value on restore).
    const originalHeight = dialog?.style.height || null;
    const originalMinHeight = dialog?.style.minHeight || null;
    const originalWidth = dialog?.style.width || null;
    const originalMinWidth = dialog?.style.minWidth || null;

    const tracked: TrackedDialog = {
      container,
      dialog,
      content: null,
      order: this.dialogOrder,
      originalHeight,
      originalMinHeight,
      originalWidth,
      originalMinWidth,
    };

    this.trackedDialogs.push(tracked);
    container.classList.add(STACKED_DIALOG_CLASS);

    this.updateAllDialogPositions();
    this.heightSyncSubject.next();
  }

  private linkDialogToContainer(dialog: HTMLElement): void {
    // Find the p-dialog-mask container
    const container = dialog.closest(
      `.${DIALOG_MASK_CLASS}`,
    ) as HTMLElement | null;
    if (!container) {
      return;
    }

    const tracked = this.trackedDialogs.find((d) => d.container === container);
    if (tracked && !tracked.dialog) {
      tracked.dialog = dialog;
      // Capture original dimensions if not already set
      if (tracked.originalHeight === null) {
        tracked.originalHeight = dialog.style.height || null;
      }
      if (tracked.originalMinHeight === null) {
        tracked.originalMinHeight = dialog.style.minHeight || null;
      }
      if (tracked.originalWidth === null) {
        tracked.originalWidth = dialog.style.width || null;
      }
      if (tracked.originalMinWidth === null) {
        tracked.originalMinWidth = dialog.style.minWidth || null;
      }
      this.updateAllDialogPositions();
      this.heightSyncSubject.next();
    }
  }

  private observeDialogContent(dialogContent: Element): void {
    const container = dialogContent.closest(
      `.${DIALOG_MASK_CLASS}`,
    ) as HTMLElement | null;
    if (!container) {
      return;
    }

    const tracked = this.trackedDialogs.find((d) => d.container === container);
    if (tracked && !tracked.content) {
      tracked.content = dialogContent;
      this.resizeObserver?.observe(dialogContent);
      dialogContent.addEventListener('scroll', this.handleScroll);
      requestAnimationFrame(() => this.checkOverflow(dialogContent));
    }
  }

  private handleScroll = (event: Event): void => {
    this.checkOverflow(event.target as Element);
  };

  private checkOverflow(dialogContent: Element): void {
    if (!dialogContent || !document.body.contains(dialogContent)) {
      return;
    }

    const stickyFooter = dialogContent.querySelector('.sticky-footer');
    if (!stickyFooter) {
      return;
    }

    const isOverflowing =
      dialogContent.scrollHeight > dialogContent.clientHeight;
    const isAtBottom =
      Math.abs(
        dialogContent.scrollHeight -
          dialogContent.clientHeight -
          dialogContent.scrollTop,
      ) < 2;

    stickyFooter.classList.toggle(
      'has-overflow-shadow',
      isOverflowing && !isAtBottom,
    );
  }

  private updateAllDialogPositions(): void {
    const totalDialogs = this.trackedDialogs.length;

    this.trackedDialogs.forEach((tracked, index) => {
      const isTopmost = index === totalDialogs - 1;
      const stackIndex = totalDialogs - 1 - index;
      // Cap the offset at MAX_STACK_LEVELS so only the last 4 dialogs move
      const cappedStackIndex = Math.min(stackIndex, MAX_STACK_LEVELS);
      const offset = cappedStackIndex * STACK_OFFSET_PX;

      // Use negative margin to shift dialog up and left
      if (tracked.dialog) {
        if (offset > 0) {
          tracked.dialog.style.marginTop = `-${offset}px`;
          tracked.dialog.style.marginLeft = `-${offset}px`;
        } else {
          tracked.dialog.style.marginTop = '';
          tracked.dialog.style.marginLeft = '';
        }
      }

      if (isTopmost) {
        tracked.container.classList.remove(INACTIVE_DIALOG_CLASS);
      } else {
        tracked.container.classList.add(INACTIVE_DIALOG_CLASS);
      }
    });
  }

  /**
   * Synchronizes the dimensions (height and width) of all open dialogs.
   * When multiple dialogs are open, ALL background dialogs match the topmost
   * dialog's dimensions, so only their stacked corner peeks behind the
   * topmost. If only one dialog is open, it restores its original dimensions.
   */
  private synchronizeDialogDimensions(): void {
    const totalDialogs = this.trackedDialogs.length;

    // If only one dialog, restore its original dimensions
    if (totalDialogs <= 1) {
      this.trackedDialogs.forEach((tracked) => {
        this.restoreOriginalDimensions(tracked);
      });
      return;
    }

    // Get the topmost dialog (last in array)
    const topmostDialog = this.trackedDialogs[totalDialogs - 1];
    if (!topmostDialog?.dialog) {
      return;
    }

    // Restore topmost dialog's original dimensions first — it may have been
    // synced to a now-closed child dialog and still carry those inline styles.
    this.restoreOriginalDimensions(topmostDialog);

    // Read the rendered dimensions of the topmost dialog after restoration
    const topmostHeight = topmostDialog.dialog.offsetHeight;
    const topmostWidth = topmostDialog.dialog.offsetWidth;

    // Synchronize ALL background dialogs to match the topmost dialog
    this.trackedDialogs.forEach((tracked, index) => {
      const isTopmost = index === totalDialogs - 1;

      if (!tracked.dialog || isTopmost) {
        return;
      }

      tracked.container.classList.add(DIMENSIONS_SYNCED_CLASS);
      tracked.dialog.style.height = `${topmostHeight}px`;
      tracked.dialog.style.minHeight = `${topmostHeight}px`;
      tracked.dialog.style.width = `${topmostWidth}px`;
      tracked.dialog.style.minWidth = `${topmostWidth}px`;
    });
  }

  /**
   * Restores the original dimensions of a tracked dialog and removes the sync class.
   */
  private restoreOriginalDimensions(tracked: TrackedDialog): void {
    if (!tracked.dialog) {
      return;
    }

    tracked.container.classList.remove(DIMENSIONS_SYNCED_CLASS);

    if (tracked.originalHeight) {
      tracked.dialog.style.height = tracked.originalHeight;
    } else {
      tracked.dialog.style.height = '';
    }

    if (tracked.originalMinHeight) {
      tracked.dialog.style.minHeight = tracked.originalMinHeight;
    } else {
      tracked.dialog.style.minHeight = '';
    }

    if (tracked.originalWidth) {
      tracked.dialog.style.width = tracked.originalWidth;
    } else {
      tracked.dialog.style.width = '';
    }

    if (tracked.originalMinWidth) {
      tracked.dialog.style.minWidth = tracked.originalMinWidth;
    } else {
      tracked.dialog.style.minWidth = '';
    }
  }

  private cleanupRemovedDialogs(element: Element): void {
    // Direct hits on the removed element (covers the legacy "mask added directly
    // to body" path, kept for safety).
    if (element.classList?.contains(DIALOG_MASK_CLASS)) {
      this.unobserveDialog(element as HTMLElement);
    }
    if (element.classList?.contains('p-dialog-content')) {
      this.unobserveDialogContent(element);
    }

    // PrimeNG v21.1.6 removes <div class="p-dialog-mask"> from inside the
    // <p-dialog> Angular component BEFORE removing the <p-dynamicdialog> host
    // from <body>. The MutationObserver here uses subtree:false, so the mask's
    // removal never reaches us directly — and by the time we see the host
    // removal, host.querySelectorAll('.p-dialog-mask') already returns empty.
    //
    // Sweep all tracked dialogs and unobserve any whose container/content is
    // no longer attached to the document. Any direct-child mutation under
    // <body> is enough to trigger this — we don't need the removed element
    // itself to still contain the mask.
    const orphanedContainers = this.trackedDialogs
      .filter((d) => !document.body.contains(d.container))
      .map((d) => d.container);
    orphanedContainers.forEach((container) => this.unobserveDialog(container));

    const orphanedContents = this.trackedDialogs
      .map((d) => d.content)
      .filter(
        (content): content is Element =>
          content !== null && !document.body.contains(content),
      );
    orphanedContents.forEach((content) => this.unobserveDialogContent(content));
  }

  private unobserveDialog(container: HTMLElement): void {
    const index = this.trackedDialogs.findIndex(
      (d) => d.container === container,
    );
    if (index === -1) {
      return;
    }

    const tracked = this.trackedDialogs[index];

    if (tracked.content) {
      tracked.content.removeEventListener('scroll', this.handleScroll);
      this.resizeObserver?.unobserve(tracked.content);
    }

    // Reset margin and dimensions
    if (tracked.dialog) {
      tracked.dialog.style.marginTop = '';
      tracked.dialog.style.marginLeft = '';
      this.restoreOriginalDimensions(tracked);
    }

    container.classList.remove(
      STACKED_DIALOG_CLASS,
      INACTIVE_DIALOG_CLASS,
      DIMENSIONS_SYNCED_CLASS,
    );

    this.trackedDialogs.splice(index, 1);

    this.updateAllDialogPositions();
    // Restore the surviving dialogs' dimensions immediately, not via the debounced
    // ResizeObserver pipeline. Otherwise the parent dialog stays frozen at the just-closed
    // child's size — and a single ResizeObserver burst (e.g. the close-animation reflow)
    // can keep resetting the debounce so the restore never lands.
    this.synchronizeDialogDimensions();
  }

  private unobserveDialogContent(dialogContent: Element): void {
    const tracked = this.trackedDialogs.find(
      (d) => d.content === dialogContent,
    );
    if (!tracked) {
      return;
    }

    dialogContent.removeEventListener('scroll', this.handleScroll);
    this.resizeObserver?.unobserve(dialogContent);
    tracked.content = null;
  }

  ngOnDestroy(): void {
    this.heightSyncSubscription?.unsubscribe();
    this.heightSyncSubject.complete();

    this.mutationObserver?.disconnect();
    this.resizeObserver?.disconnect();

    this.trackedDialogs.forEach((tracked) => {
      if (tracked.content) {
        tracked.content.removeEventListener('scroll', this.handleScroll);
      }
      // Reset margin and dimensions
      if (tracked.dialog) {
        tracked.dialog.style.marginTop = '';
        tracked.dialog.style.marginLeft = '';
        this.restoreOriginalDimensions(tracked);
      }
      tracked.container.classList.remove(
        STACKED_DIALOG_CLASS,
        INACTIVE_DIALOG_CLASS,
        DIMENSIONS_SYNCED_CLASS,
      );
    });

    this.trackedDialogs = [];

    if (this.styleElement && this.styleElement.parentNode) {
      this.styleElement.parentNode.removeChild(this.styleElement);
      this.styleElement = null;
    }
  }
}
